import { motion } from "framer-motion";
import { Award, Sparkles, RotateCcw, ArrowRight, CheckCircle2, XCircle, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import BilingualText from "@/components/BilingualText";

/**
 * End-of-session recap.
 * Props:
 *   correct: number, total: number, skill: string, level: string
 *   onRestart: () => void, onDashboard: () => void
 */
export function SessionSummary({ correct, total, skill, level, onRestart, onDashboard }) {
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;

  // Simple badge logic
  const badges = [];
  if (total >= 5) badges.push({ icon: Sparkles, label_cn: "完成一局", label_en: "Session done", color: "bg-sky-100 text-sky-700" });
  if (pct >= 60) badges.push({ icon: CheckCircle2, label_cn: "半数以上正确", label_en: "Solid effort", color: "bg-emerald-100 text-emerald-700" });
  if (pct >= 80) badges.push({ icon: Award, label_cn: "高手风范", label_en: "Sharp shooter", color: "bg-amber-100 text-amber-700" });
  if (pct === 100 && total >= 5) badges.push({ icon: Flame, label_cn: "满分！", label_en: "Perfect score!", color: "bg-rose-100 text-rose-700" });

  const message =
    pct >= 80 ? { cn: "太棒了！继续保持！", en: "Amazing — keep it up!" } :
    pct >= 60 ? { cn: "做得不错，再练一轮就更好！", en: "Nice work — one more round to level up." } :
    pct >= 40 ? { cn: "打好基础很重要，慢慢来。", en: "Steady progress — building the basics." } :
                { cn: "别灰心，多练几遍就会更好。", en: "Don't give up — practice makes perfect." };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="card-plush p-6 sm:p-10 max-w-2xl mx-auto text-center space-y-8"
      data-testid="session-summary"
    >
      <div className="flex flex-col items-center gap-3">
        <span className="badge-soft">
          {skill} · {level}
        </span>
        <BilingualText
          cn="本次练习总结"
          en="Session summary"
          size="hero"
          className="items-center"
        />
      </div>

      {/* Big score */}
      <div className="relative inline-flex flex-col items-center">
        <div className="serif text-7xl sm:text-8xl font-black text-slate-900 leading-none">
          {pct}
          <span className="text-3xl sm:text-4xl text-slate-400 align-top">%</span>
        </div>
        <div className="mt-2 text-slate-500 font-semibold">
          {correct} / {total} correct · 答对 {correct} / {total}
        </div>
      </div>

      {/* Encouragement */}
      <div className="bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 rounded-2xl p-4">
        <BilingualText cn={message.cn} en={message.en} size="md" className="items-center" />
      </div>

      {/* Badges */}
      {badges.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs font-black text-slate-500 uppercase tracking-widest">
            Badges · 徽章
          </div>
          <div className="flex flex-wrap justify-center gap-2" data-testid="session-badges">
            {badges.map((b, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 + i * 0.1, type: "spring", stiffness: 220 }}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-black ${b.color}`}
                data-testid={`badge-${b.label_en.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <b.icon className="w-4 h-4" />
                <span className="cn">{b.label_cn}</span>
                <span>· {b.label_en}</span>
              </motion.span>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="flex flex-wrap justify-center gap-3">
        <Button
          size="lg"
          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
          onClick={onRestart}
          data-testid="session-restart-btn"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          <span className="cn mr-1">再来一轮</span>Practise again
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="rounded-full font-bold"
          onClick={onDashboard}
          data-testid="session-dashboard-btn"
        >
          <ArrowRight className="w-4 h-4 mr-2" />
          <span className="cn mr-1">返回首页</span>Back to dashboard
        </Button>
      </div>
    </motion.div>
  );
}

export default SessionSummary;
